import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { OnboardingWizard } from '@/features/onboarding/OnboardingWizard';
import { ParentalConsentGate } from '@/components/consent/ParentalConsentGate';

export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [showParentalConsent, setShowParentalConsent] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  useEffect(() => {
    if (authLoading || !user) {
      setCheckingOnboarding(false);
      return;
    }

    const checkOnboarding = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('onboarded, is_minor, parental_consent_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[OnboardingGate] Error checking onboarding:', error);
          setCheckingOnboarding(false);
          return;
        }

        const profileAny = profile as any;

        if (profileAny?.is_minor && !profileAny?.parental_consent_at) {
          setShowParentalConsent(true);
          setShowWizard(false);
        } else {
          setShowParentalConsent(false);
          setShowWizard(!profile?.onboarded);
        }

        setCheckingOnboarding(false);
      } catch (error) {
        console.error('[OnboardingGate] Error checking onboarding:', error);
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [user, authLoading]);

  const handleWizardComplete = () => {
    setShowWizard(false);
    window.location.reload();
  };

  const handleParentalConsentComplete = () => {
    setShowParentalConsent(false);
    window.location.reload();
  };

  if (authLoading || checkingOnboarding) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (showParentalConsent) {
    return <ParentalConsentGate onConsentComplete={handleParentalConsentComplete} />;
  }

  return (
    <>
      {showWizard && <OnboardingWizard isOpen={showWizard} onComplete={handleWizardComplete} />}
      {children}
    </>
  );
}

